import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Content } from '../../types/content';
import ContentActions from '../common/ContentActions';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { Play, Pause, Volume2, VolumeX, Maximize2 } from 'lucide-react';
import * as Icons from 'lucide-react';

interface ContentViewerProps {
  content: Content;
  onLike: () => void;
  onComment: () => void;
  onShare: () => void;
}

export default function ContentViewer({ content, onLike, onComment, onShare }: ContentViewerProps) {
  const { theme } = useTheme();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const CategoryIcon = Icons[content.category.icon as keyof typeof Icons];

  const renderVideoPlayer = () => {
    if (content.type !== 'video') return null;
    
    return (
      <div className="relative aspect-video mb-6 group">
        <video
          src={content.videoUrl}
          poster={content.thumbnailUrl}
          className="w-full h-full rounded-lg"
          controls={false}
          muted={isMuted}
        />
        
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="absolute bottom-0 left-0 right-0 p-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              >
                {isPlaying ? (
                  <Pause className="h-6 w-6 text-white" />
                ) : (
                  <Play className="h-6 w-6 text-white" />
                )}
              </button>
              
              <button
                onClick={() => setIsMuted(!isMuted)}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              >
                {isMuted ? (
                  <VolumeX className="h-6 w-6 text-white" />
                ) : (
                  <Volume2 className="h-6 w-6 text-white" />
                )}
              </button>
            </div>
            
            <button className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors">
              <Maximize2 className="h-6 w-6 text-white" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <article className={`
      rounded-lg overflow-hidden transition-all duration-200
      ${theme === 'dark' 
        ? 'bg-gray-800 shadow-lg shadow-gray-900/20'
        : 'bg-white shadow-md'
      }
    `}>
      {content.type === 'article' && content.coverImage && (
        <img 
          src={content.coverImage}
          alt={content.title}
          className="w-full h-[400px] object-cover"
        />
      )}
      
      {content.type === 'video' && renderVideoPlayer()}
      
      <div className="p-8">
        <header className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <img
                src={content.author.avatar}
                alt={content.author.name}
                className="w-12 h-12 rounded-full ring-2 ring-blue-500/20"
              />
              <div>
                <p className={`font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
                  {content.author.name}
                </p>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {formatDistanceToNow(content.createdAt, { addSuffix: true })}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 text-blue-600">
              {CategoryIcon && <CategoryIcon className="h-5 w-5" />}
              <span className="text-sm font-medium">{content.category}</span>
            </div>
          </div>
          
          <h1 className={`text-3xl font-bold mb-4 ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            {'title' in content ? content.title : ''}
          </h1>
          
          <div className="flex items-center justify-between">
            <ContentActions
              content={content}
              onLike={onLike}
              onComment={onComment}
              onShare={onShare}
            />
          </div>
        </header>

        <div className={`prose max-w-none ${theme === 'dark' ? 'prose-invert' : ''}`}>
          {'content' in content && (
            <div dangerouslySetInnerHTML={{ __html: content.content.replace(/\n/g, '<br>') }} />
          )}
        </div>

        {content.tags && content.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-8">
            {content.tags.map(tag => (
              <Link
                key={tag}
                to={`/tag/${tag}`}
                className={`
                  text-sm px-3 py-1 rounded-full transition-colors
                  ${theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                #{tag}
              </Link>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}